import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { documentationPages, patterns, SITE_URL } from '../../content/system'
import { components } from '../../content/components'
import { missionAt, missions } from '../../content/missions'
import { pageTemplates } from '../../content/templates'
import { useBrand } from '@/state/useBrand'

export default function ScrollToTop() {
  const { pathname, hash } = useLocation()
  const previousPath = useRef(pathname)
  const { name } = useBrand()
  // The description index.html ships with, for pages without one of their own.
  const defaultDescription = useRef(
    typeof document === 'undefined' ? '' : (document.head.querySelector('meta[name="description"]')?.getAttribute('content') ?? ''),
  )
  useEffect(() => {
    // Standalone pages, a template preview or the spec sheet, name themselves.
    if (pathname.endsWith('/preview') || pathname === '/design-tools/spec') return
    const title =
      (missionAt(pathname) ? name : undefined) ??
      documentationPages.find((page) => page.path === pathname)?.title ??
      patterns.find((pattern) => `/patterns/${pattern.id}` === pathname)
        ?.title ??
      components.find((component) => `/components/${component.id}` === pathname)?.name ??
      missions.find((mission) => `/missions/${mission.id}` === pathname)?.name ??
      pageTemplates.find((template) => `/templates/${template.id}` === pathname)?.name ??
      (
        {
          '/search': 'Search',
          '/privacy': 'Privacy',
          '/disclaimer': 'Disclaimer',
        } as Record<string, string>
      )[pathname] ??
      'Page not found'
    document.title = `${title} | MDS`
    // Search engines and link previews read the page's own address and
    // description, not the home page's.
    const home = missionAt(pathname)
    const description =
      (home && home.id !== 'america' ? home.mission.text : undefined) ??
      documentationPages.find((page) => page.path === pathname)?.description ??
      patterns.find((pattern) => `/patterns/${pattern.id}` === pathname)?.summary ??
      components.find((component) => `/components/${component.id}` === pathname)?.summary ??
      missions.find((mission) => `/missions/${mission.id}` === pathname)?.mission.text ??
      pageTemplates.find((template) => `/templates/${template.id}` === pathname)?.summary ??
      defaultDescription.current
    const found = title !== 'Page not found'
    const head = (selector: string, attribute: string, value: string) => document.head.querySelector(selector)?.setAttribute(attribute, value)
    head('link[rel="canonical"]', 'href', `${SITE_URL}${found ? pathname : '/'}`)
    head('meta[property="og:url"]', 'content', `${SITE_URL}${pathname}`)
    head('meta[name="description"]', 'content', description)
    head('meta[property="og:description"]', 'content', description)
    head('meta[name="twitter:description"]', 'content', description)
    head('meta[property="og:title"]', 'content', `${title} | Mission Design System`)
    head('meta[name="twitter:title"]', 'content', `${title} | Mission Design System`)
    // A missing page asks not to be indexed.
    let robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]')
    if (!found && !robots) {
      robots = Object.assign(document.createElement('meta'), { name: 'robots', content: 'noindex' })
      document.head.append(robots)
    } else if (found) robots?.remove()
  }, [pathname, name])
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    if (previousPath.current !== pathname) {
      document.getElementById('main')?.focus({ preventScroll: true })
      previousPath.current = pathname
    }
    // A link to a section, such as /disclaimer#previews, opens at that section.
    if (hash) window.requestAnimationFrame(() => document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView())
  }, [pathname, hash])
  return null
}
